<template>
  <div class="form-container">
    <h1 class="page-title">顧客マスタ情報新規作成</h1>
    <form @submit.prevent="handleSubmit" @keydown.enter.prevent>

      <div class="mb-3">
        <label for="customerName" class="form-label">顧客名 <span class="badge text-bg-danger">必須</span></label>
        <textarea
          v-model="customer.customer_nm"
          placeholder="顧客名"
          class="form-control"
          id="customerName"
          required
          rows="2"
          @keydown.enter.stop
        ></textarea>
        <div v-if="errorMessages.customer_nm" class="alert alert-danger mt-2">{{ errorMessages.customer_nm[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="customerNo" class="form-label">顧客番号</label>
        <input v-model="customer.customer_no" type="text" placeholder="顧客番号" class="form-control" id="customerNo">
        <div v-if="errorMessages.customer_no" class="alert alert-danger mt-2">{{ errorMessages.customer_no[0] }}</div>
      </div>

      <div class="mb-3">
        <label class="form-label">所属する本社</label>
        <CustomerSelectBox
          v-model="customer.parent_customer_id"
          :options="parentCustomerOptions"
          placeholder="支店として登録する場合のみ、本社名またはIDで検索"
        />
        <div class="form-text">
          本社または単独顧客として登録する場合は未設定にしてください。売上の「本社別（支店含む）」集計にのみ使用します。
        </div>
        <div v-if="errorMessages.parent_customer_id" class="alert alert-danger mt-2">{{ errorMessages.parent_customer_id[0] }}</div>
      </div>

      <PostalCodeInput
        v-model="customer.postal_code"
        @address="({ region, locality, street }) => {
          customer.prefecture = region;
          customer.city = locality;
          customer.other_address = street;
        }"
      />

      <div class="mb-3">
        <label for="prefecture" class="form-label">都道府県</label>
        <input v-model="customer.prefecture" type="text" placeholder="都道府県" class="form-control" id="prefecture">
        <div v-if="errorMessages.prefecture" class="alert alert-danger mt-2">{{ errorMessages.prefecture[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="city" class="form-label">市区町村</label>
        <input v-model="customer.city" type="text" placeholder="市区町村" class="form-control" id="city">
        <div v-if="errorMessages.city" class="alert alert-danger mt-2">{{ errorMessages.city[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="otherAddress" class="form-label">その他の住所</label>
        <input v-model="customer.other_address" type="text" placeholder="その他の住所" class="form-control" id="otherAddress">
        <div v-if="errorMessages.other_address" class="alert alert-danger mt-2">{{ errorMessages.other_address[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="telNum" class="form-label">電話番号</label>
        <input v-model="customer.tel_num" type="text" placeholder="電話番号" class="form-control" id="telNum">
        <div v-if="errorMessages.tel_num" class="alert alert-danger mt-2">{{ errorMessages.tel_num[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="fax" class="form-label">FAX</label>
        <input v-model="customer.fax" type="text" placeholder="FAX" class="form-control" id="fax">
        <div v-if="errorMessages.fax" class="alert alert-danger mt-2">{{ errorMessages.fax[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="presidentNm" class="form-label">代表者名</label>
        <input v-model="customer.president_nm" type="text" placeholder="代表者名" class="form-control" id="presidentNm">
        <div v-if="errorMessages.president_nm" class="alert alert-danger mt-2">{{ errorMessages.president_nm[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="billingTimingType" class="form-label">入金予定</label>
        <select class="form-select" v-model="customer.billing_timing_type">
          <option value="">選択してください</option>
          <option value="0">翌月15日</option>
          <option value="1">翌月25日</option>
          <option value="2">翌月末</option>
          <option value="3">翌々月5日</option>
          <option value="4">翌々月10日</option>
        </select>
      </div>

      <div class="mb-3">
        <label for="billingDeadline" class="form-label">請求締切</label>
        <select class="form-select" v-model="customer.billing_deadline">
          <option value="">選択してください</option>
          <option value="0">5日</option>
          <option value="1">10日</option>
          <option value="2">15日</option>
          <option value="3">20日</option>
          <option value="4">25日</option>
          <option value="5">月末</option>
        </select>
      </div>

      <div class="mb-3">
        <label class="form-label">振込先銀行</label>
        <div v-if="banks.length === 0" class="text-muted">銀行マスタが未登録です。</div>
        <div v-else>
          <div
            v-for="bank in banks"
            :key="bank.id"
            class="form-check mb-1"
          >
            <label class="ECM_CheckboxInput-sm" :for="`bank_${bank.id}`">
              <input
                class="ECM_CheckboxInput-Input-sm"
                type="checkbox"
                :id="`bank_${bank.id}`"
                :value="bank.id"
                v-model="selectedBankIds"
              >
              <span class="ECM_CheckboxInput-DummyInput-sm me-2"></span>
              {{ bank.bank_nm }} {{ bank.branch_nm }} {{ bank.account_type }} {{ bank.account_no }}
            </label>
          </div>
        </div>
      </div>

      <div class="btn-area">
        <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        <button type="submit" class="btn btn-primary mx-1" :disabled="isSubmitting">登録</button>
      </div>
    </form>

    <DuplicateNameConfirmationModal
      :modalId="duplicateModalId"
      message="すでに同名の顧客名が登録されていますが、本当に作成してもよろしいでしょうか？"
      @confirm="handleDuplicateConfirm"
    />
  </div>
</template>

<script>
import { computed, ref, watchEffect, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { Modal } from 'bootstrap';
import { checkCustomerDuplicate, createCustomer, getSelectBoxBanks, getSelectBoxCustomers } from '@/api/axios';
import DuplicateNameConfirmationModal from '@/components/shared/DuplicateNameConfirmationModal.vue';
import PostalCodeInput from '@/components/shared/PostalCodeInput.vue';
import CustomerSelectBox from '@/components/searchSelectBoxes/CustomerSelectBox.vue';

export default {
  components: {
    DuplicateNameConfirmationModal,
    PostalCodeInput,
    CustomerSelectBox
  },
  setup() {
    const title = ref('顧客情報新規作成');

    watchEffect(() => {
      document.title = title.value;
    });

    const customer = ref({
      customer_nm: '',
      customer_no: '',
      parent_customer_id: null,
      postal_code: '',
      prefecture: '',
      city: '',
      other_address: '',
      tel_num: '',
      fax: '',
      president_nm: '',
      billing_nm: '',
      billing_timing_type: '',
      billing_deadline: ''
      // 他のフィールド
    });

    const banks = ref([]);
    const customers = ref([]);
    const selectedBankIds = ref([]);
    const parentCustomerOptions = computed(() => {
      return customers.value.filter((candidate) => candidate.parent_customer_id == null);
    });

    // エラーメッセージ用のリアクティブプロパティ
    const errorMessages = ref({});
    const isSubmitting = ref(false);
    const duplicateModalId = 'duplicateCustomerCreateModal';

    const store = useStore();
    const router = useRouter();
    const route = useRoute();

    const showDuplicateModal = () => {
      const modalElement = document.getElementById(duplicateModalId);
      const modal = Modal.getInstance(modalElement) || new Modal(modalElement);
      modal.show();
    };

    const saveCustomer = async () => {
      if (isSubmitting.value) return;

      try {
        isSubmitting.value = true;
        customer.value.m_bank_ids = selectedBankIds.value;
        await createCustomer(customer.value);
        store.dispatch('setFlashMessage', {message: '顧客情報作成完了！', type: 'success'});
        router.push({
          path: '/customers',
          query: route.query
        });
      } catch (error) {
        if (error.response && error.response.data) {
          // エラーメッセージをerrorMessagesにセット
          errorMessages.value = error.response.data || {};
        }
        store.dispatch('setFlashMessage', {message: '顧客情報作成失敗！', type: 'danger'});
        console.error('作成エラー:', error);
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleSubmit = async () => {
      if (isSubmitting.value) return;

      try {
        isSubmitting.value = true;
        const response = await checkCustomerDuplicate(customer.value.customer_nm);
        if (response.data?.duplicate) {
          showDuplicateModal();
          return;
        }
      } catch (error) {
        console.error('重複チェックエラー:', error);
      } finally {
        isSubmitting.value = false;
      }

      await saveCustomer();
    };

    const handleDuplicateConfirm = async () => {
      await saveCustomer();
    };

    const fetchBanks = async () => {
      try {
        const response = await getSelectBoxBanks();
        banks.value = response.data || [];
      } catch (error) {
        console.error('銀行マスタの取得に失敗しました:', error);
      }
    };

    const fetchCustomers = async () => {
      try {
        const response = await getSelectBoxCustomers();
        customers.value = response.data || [];
      } catch (error) {
        console.error('親顧客候補の取得に失敗しました:', error);
      }
    };

    onMounted(() => {
      fetchBanks();
      fetchCustomers();
    });

    return {
      title,
      customer,
      handleSubmit,
      handleDuplicateConfirm,
      errorMessages,
      isSubmitting,
      duplicateModalId,
      banks,
      parentCustomerOptions,
      selectedBankIds
    };
  }
};
</script>
